<template>
  <LcLotteryNumbers
    v-bind="{ fields, ...$attrs }"
    class="high-frequency-game-animated-numbers flex"
    :class="[animate, { 'no-animation': noAnimation }]"
    list-class="animated-numbers-list"
    :number-class="getNumberClass"
  >
    <template v-for="field in fields" :key="field.name" #[`number-${field.name}`]="{ number }">
      <span
        class="number-slot-text"
        :style="{
          '--slide-up-delay': `${field.numbers.findIndex((n) => n.value === number) * slideUpStaggerMs}ms`,
          '--slide-up-duration': `${slideUpDurationMs}ms`,
        }"
      >
        {{ number }}
      </span>
    </template>
  </LcLotteryNumbers>
</template>

<script lang="ts" setup>
import type { LcLotteryNumber, LcLotteryNumbersField } from '@lottocom/frontend-components'

type Animation = 'match' | 'show' | 'slide-up'

type Props = {
  animate?: Animation
  fields: LcLotteryNumbersField[]
  noAnimation?: boolean
  revealedNumbers?: LcLotteryNumber['value'][]
  slideUpDurationMs?: number
  slideUpStaggerMs?: number
}

const CSS_CLASS_NUMBER = 'number'
const CSS_CLASS_NUMBER_HIDDEN = 'number-hidden'
const CSS_CLASS_NUMBER_SHOW = 'number-show'

const {
  animate = 'show',
  fields,
  noAnimation = false,
  revealedNumbers = [],
  slideUpDurationMs = 250,
  slideUpStaggerMs = 15,
} = defineProps<Props>()

const getNumberClass = (value: LcLotteryNumber['value'], name: string) => {
  const fieldGroup = fields.find((field) => field.name.toLowerCase().replace('_', '-') === name)
  if (!fieldGroup) {
    return {
      [CSS_CLASS_NUMBER]: true,
      [CSS_CLASS_NUMBER_HIDDEN]: true,
      [CSS_CLASS_NUMBER_SHOW]: false,
    }
  }

  const show = revealedNumbers.includes(value)

  return {
    [CSS_CLASS_NUMBER]: true,
    [CSS_CLASS_NUMBER_HIDDEN]: !show,
    [CSS_CLASS_NUMBER_SHOW]: show,
  }
}
</script>

<style lang="scss" scoped>
@use 'shared/style' as *;

.high-frequency-game-animated-numbers {
  :deep(.number) {
    color: var(--lottery-numbers-main-color, #000);

    /* stylelint-disable-next-line selector-class-pattern */
    .lc-lottery-numbers__icon-wrap {
      transition: scale $hfg-duration-checkmark cubic-bezier(0.34, 1.56, 0.64, 1);
      scale: 0;
    }
  }

  &.match {
    :deep(.number-show) {
      animation: glow $hfg-duration-glow ease-out forwards;

      /* stylelint-disable-next-line selector-class-pattern */
      .lc-lottery-numbers__icon-wrap {
        transition-delay: $hfg-duration-glow;
        scale: 1;
      }
    }
  }

  &.show {
    $show-delay: 1.5s;

    min-height: 3rem;
    margin: 0 0 -#{token('size/space/gap/100')} -#{token('size/space/gap/050')};
    padding-top: token('size/space/gap/100');

    :deep(.number) {
      $width-delay: calc($show-delay + 0.3s);

      margin: 0;
      opacity: 0;
      transition:
        width $hfg-duration-width cubic-bezier(0.16, 1, 0.3, 1) $width-delay,
        margin $hfg-duration-width cubic-bezier(0.16, 1, 0.3, 1) $width-delay;
    }

    :deep(.animated-numbers-list) {
      gap: token('size/space/gap/none');
    }

    :deep(.number-hidden) {
      width: 0;
    }

    :deep(.number-show) {
      margin: 0 0 token('size/space/gap/100') token('size/space/gap/100');
      animation: roll-in $hfg-duration-roll-in-sm $hfg-easing-roll-in forwards
        ($hfg-duration-width + $show-delay);

      /* stylelint-disable-next-line selector-class-pattern */
      .lc-lottery-numbers__icon-wrap {
        transition-delay: $hfg-duration-glow + $show-delay;
        scale: 1;
      }
    }
  }

  &.slide-up {
    :deep(.number) {
      overflow: hidden;
    }

    :deep(.number-slot-text) {
      opacity: 0;
      animation: slide-up var(--slide-up-duration, 0.45s) ease forwards;
      animation-delay: var(--slide-up-delay, 0ms);
    }
  }

  &.no-animation {
    :deep(.number) {
      opacity: 1;
      transition: none;
      animation: none;
    }

    :deep(.number-slot-text) {
      opacity: 1;
      animation: none;
    }

    :deep(.number-show) {
      animation: none;

      /* stylelint-disable-next-line selector-class-pattern */
      .lc-lottery-numbers__icon-wrap {
        transition: none;
        scale: 1;
      }
    }
  }

  @keyframes slide-up {
    from {
      opacity: 0;
      transform: translateY(100%);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes glow {
    50% {
      box-shadow: var(--drawn-ball-highlight, 0 0 1.75rem 0 #fdd454);
    }
  }

  @keyframes roll-in {
    0% {
      opacity: 0;
      transform: translateX(100%) rotate(90deg);
    }

    70%,
    100% {
      opacity: 1;
      transform: translateX(0) rotate(0deg);
    }
  }
}
</style>
